import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { semanticCapabilities } from '../data/semanticKnowledge';
import { fadeUp } from '../utils/motion';
import { PageHero } from '../components/layout/PageHero';
import { SkpLlmTeaser } from '../components/semantic/SkpLlmTeaser';
import { SkpWorkflow } from '../components/semantic/SkpWorkflow';
import { SkpResearch } from '../components/semantic/SkpResearch';
import { SkpCapabilityFoundation } from '../components/semantic/SkpCapabilityFoundation';
import { SkpControls } from '../components/semantic/SkpControls';
import { SkpFoundation } from '../components/semantic/SkpFoundation';
import { SkpCTA } from '../components/semantic/SkpCTA';
export function SemanticKnowledgePage() {
  return <>
      <PageHero eyebrow="Products" title="Semantic Knowledge Platform" lead="The knowledge infrastructure that connects and governs concepts, relationships, sources, and approved knowledge, with knowledge graphs, semantic search, and AI-assisted research." body={['It turns scattered research and content into connected, usable knowledge — defining concepts and relationships, linking evidence across sources, and pairing AI-assisted discovery with expert review.']} data-id="element-2162" data-mp="qyeffx6oqyej">
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8" data-id="element-2163" data-mp="k1xuaqe2klay">
          <a href="/contact" className="inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2164" data-mp="1howgu8b1scv4">
            Request a Demo
            <ArrowRight size={18} aria-hidden="true" data-id="element-2165" data-mp="12zqo936ptkw7" />
          </a>
          <a href="/semantic-knowledge#how-it-works" onClick={event => {
          event.preventDefault();
          document.getElementById('how-it-works')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }} className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2166" data-mp="12zgwba2413nv">
            See how it works
          </a>
        </div>
      </PageHero>

      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-2167" data-mp="3g66ptbvsknuk">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2168" data-mp="3l4roh6veazgw">
          <motion.h2 {...fadeUp} className="lg:col-span-4 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight leading-tight" data-id="element-2169" data-mp="374z2tpyq79fs">
            What the platform brings together
          </motion.h2>
          <motion.dl {...fadeUp} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 border-t border-line" data-id="element-2170" data-mp="326nu5ik634z5">
            {semanticCapabilities.map(item => <div key={item.title} className="border-b border-line py-7" data-id="element-2171" data-mp="10f2dj4xtbxth">
                <dt className="font-heading font-bold text-xl text-ink-dark mb-2" data-id="element-2172" data-mp="10cyasqbnh4us">{item.title}</dt>
                <dd className="text-ink-muted leading-relaxed" data-id="element-2173" data-mp="2kv0fimldjhj6">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      <SkpLlmTeaser data-id="element-2174" data-mp="2uny3v9d1s0pp" />
      <SkpWorkflow data-id="element-2175" data-mp="2uoha3stc50nw" />
      <SkpResearch data-id="element-2176" data-mp="2uozbf3uuu52o" />

      <section aria-labelledby="skp-noetivra" className="bg-surface-bg border-y border-line py-20 lg:py-28" data-id="element-2177" data-mp="2pvwimxxwiznj">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2178" data-mp="33n1nceg4mi4q">
          <div className="lg:col-span-5" data-id="element-2179" data-mp="2yr433x1qsrmo">
            <p className="text-sm font-medium text-teal mb-3" data-id="element-2180" data-mp="3dkz98vaik2u6">Noetivra · Coming soon</p>
            <h2 id="skp-noetivra" className="font-heading font-bold text-3xl lg:text-4xl text-ink-dark tracking-tight leading-tight" data-id="element-2181" data-mp="38mnzslo4ruch">
              Explore connected knowledge through Noetivra
            </h2>
          </div>
          <div className="lg:col-span-7" data-id="element-2182" data-mp="1eb99a2napqvo">
            <p className="text-xl text-ink-dark leading-relaxed mb-6" data-id="element-2183" data-mp="5awnz9ads9wz">
              Noetivra would provide interactive views of knowledge maintained in
              SKP. Users could navigate relationships, inspect supporting
              passages, compare findings, annotate questions, and participate in
              authorized review workflows.
            </p>
            <a href="/noetivra#research" className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2184" data-mp="3iquk7bxsqc37">
              Explore Noetivra
              <ArrowRight size={16} aria-hidden="true" data-id="element-2185" data-mp="3rpzlur4nmljl" />
            </a>
          </div>
        </motion.div>
      </section>

      <SkpCapabilityFoundation data-id="element-2186" data-mp="ezjq1ce6u0k" />
      <SkpControls data-id="element-2187" data-mp="300wgf811755d" />
      <SkpFoundation data-id="element-2188" data-mp="2zzbm9jimpo81" />
      <SkpCTA data-id="element-2189" data-mp="2zzv28uopy2jf" />
    </>;
}